// File: /var/www/tpcrm/frontend/src/components/Agent/AgentDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  analyticsService, 
  clientsService, 
  quotesService, 
  commissionsService,
  itinerariesService,
  paymentRemindersService 
} from '../../services';
import { useResponsive } from '../../hooks/useResponsive';
import theme from '../../styles/travelieTheme';
import './AgentDashboard.css';
import './AgentDashboard-Mobile.css';

const AgentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isDesktopOrWide } = useResponsive();
  const [dashboardData, setDashboardData] = useState(null);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [paymentReminders, setPaymentReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredQuoteId, setHoveredQuoteId] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data using services
      const [dashboard, clientsData, quotesData, commissionsData, itinerariesData, remindersData] = await Promise.all([
        analyticsService.getDashboard(),
        clientsService.getAll(),
        quotesService.getAll(),
        commissionsService.getAll(),
        itinerariesService.getAll(),
        paymentRemindersService.getAll()
      ]);

      setDashboardData(dashboard);
      setClients(clientsData);
      setQuotes(quotesData);
      setCommissions(commissionsData);
      setItineraries(itinerariesData);
      setPaymentReminders(remindersData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async (quoteData) => {
    try {
      const newQuote = await quotesService.create(quoteData);
      setQuotes([...quotes, newQuote]);
      return newQuote;
    } catch (error) {
      console.error('Error creating quote:', error);
      throw error;
    }
  };

  const handleUpdateQuote = async (quoteId, quoteData) => {
    try {
      const updatedQuote = await quotesService.update(quoteId, quoteData);
      setQuotes(quotes.map(q => q.id === quoteId ? updatedQuote : q));
      return updatedQuote;
    } catch (error) {
      console.error('Error updating quote:', error);
      throw error;
    }
  };

  const handleSendQuote = async (quoteId, emailData) => {
    try {
      await quotesService.send(quoteId, emailData);
      // Update quote status locally
      setQuotes(quotes.map(q => 
        q.id === quoteId ? { ...q, status: 'sent' } : q
      ));
    } catch (error) {
      console.error('Error sending quote:', error);
      throw error;
    }
  };

  const handleConvertToItinerary = async (quoteId) => {
    try {
      const newItinerary = await quotesService.convertToItinerary(quoteId);
      // Update local state
      setItineraries([...itineraries, newItinerary]);
      setQuotes(quotes.map(q => 
        q.id === quoteId ? { ...q, status: 'converted' } : q
      ));
      return newItinerary;
    } catch (error) {
      console.error('Error converting quote:', error);
      throw error;
    }
  };

  const handleSubmitCommission = async (commissionData) => {
    try {
      const newCommission = await commissionsService.submit(commissionData);
      setCommissions([...commissions, newCommission]);
      return newCommission;
    } catch (error) {
      console.error('Error submitting commission:', error);
      throw error;
    }
  };

  const handleCreatePaymentReminder = async (reminderData) => {
    try {
      const newReminder = await paymentRemindersService.create(reminderData);
      setPaymentReminders([...paymentReminders, newReminder]);
      return newReminder;
    } catch (error) {
      console.error('Error creating payment reminder:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{
        fontSize: theme.typography.fontSize['2xl'],
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text.primary,
        marginBottom: theme.spacing['2xl']
      }}>
        Dashboard Overview
      </h2>
      
      {/* Stats Grid - Horizontal Row */}
      <div 
        className="dashboard-stats-grid"
        style={{
          gap: theme.spacing.lg,
          marginBottom: theme.spacing['3xl']
        }}
      >
              <div style={{
                background: theme.colors.neutral.card,
                borderRadius: theme.components.statCard.borderRadius,
                padding: theme.components.statCard.padding,
                boxShadow: theme.components.statCard.shadow
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
                  <div style={{
                    flexShrink: 0,
                    background: theme.getStatIconColor('clients'),
                    borderRadius: theme.components.statCard.iconBorderRadius,
                    padding: theme.components.statCard.iconPadding,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg style={{ 
                      height: theme.components.statCard.iconSize, 
                      width: theme.components.statCard.iconSize,
                      color: theme.colors.text.white 
                    }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <p style={{
                      color: theme.colors.text.secondary,
                      fontSize: theme.typography.fontSize.sm,
                      marginBottom: '4px'
                    }}>
                      Total Clients
                    </p>
                    <p style={{
                      fontSize: theme.typography.fontSize['3xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.text.primary
                    }}>
                      {clients.length}
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                background: theme.colors.neutral.card,
                borderRadius: theme.components.statCard.borderRadius,
                padding: theme.components.statCard.padding,
                boxShadow: theme.components.statCard.shadow
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
                  <div style={{
                    flexShrink: 0,
                    background: theme.getStatIconColor('quotes'),
                    borderRadius: theme.components.statCard.iconBorderRadius,
                    padding: theme.components.statCard.iconPadding,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg style={{ 
                      height: theme.components.statCard.iconSize, 
                      width: theme.components.statCard.iconSize,
                      color: theme.colors.text.white 
                    }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p style={{
                      color: theme.colors.text.secondary,
                      fontSize: theme.typography.fontSize.sm,
                      marginBottom: '4px'
                    }}>
                      Active Quotes
                    </p>
                    <p style={{
                      fontSize: theme.typography.fontSize['3xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.text.primary
                    }}>
                      {quotes.filter(q => q.status === 'draft' || q.status === 'sent').length}
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                background: theme.colors.neutral.card,
                borderRadius: theme.components.statCard.borderRadius,
                padding: theme.components.statCard.padding,
                boxShadow: theme.components.statCard.shadow
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
                  <div style={{
                    flexShrink: 0,
                    background: theme.getStatIconColor('itineraries'),
                    borderRadius: theme.components.statCard.iconBorderRadius,
                    padding: theme.components.statCard.iconPadding,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg style={{ 
                      height: theme.components.statCard.iconSize, 
                      width: theme.components.statCard.iconSize,
                      color: theme.colors.text.white 
                    }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p style={{
                      color: theme.colors.text.secondary,
                      fontSize: theme.typography.fontSize.sm,
                      marginBottom: '4px'
                    }}>
                      Active Itineraries
                    </p>
                    <p style={{
                      fontSize: theme.typography.fontSize['3xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.text.primary
                    }}>
                      {itineraries.length}
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                background: theme.colors.neutral.card,
                borderRadius: theme.components.statCard.borderRadius,
                padding: theme.components.statCard.padding,
                boxShadow: theme.components.statCard.shadow
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
                  <div style={{
                    flexShrink: 0,
                    background: theme.getStatIconColor('commissions'),
                    borderRadius: theme.components.statCard.iconBorderRadius,
                    padding: theme.components.statCard.iconPadding,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg style={{ 
                      height: theme.components.statCard.iconSize, 
                      width: theme.components.statCard.iconSize,
                      color: theme.colors.text.white 
                    }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p style={{
                      color: theme.colors.text.secondary,
                      fontSize: theme.typography.fontSize.sm,
                      marginBottom: '4px'
                    }}>
                      Pending Commissions
                    </p>
                    <p style={{
                      fontSize: theme.typography.fontSize['3xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.text.primary
                    }}>
                      {commissions.filter(c => c.status === 'pending').length}
                    </p>
                  </div>
                </div>
              </div>
            </div>


            {/* Two-Column Layout: Recent Activity (70%) + Quick Actions Sidebar (30%) */}
            <div 
              className="dashboard-two-column"
              style={{
                gap: theme.spacing['2xl']
              }}
            >
              {/* Left Column: Recent Activity */}
              <div>
                <h3 style={{
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.semibold,
                  color: theme.colors.text.primary,
                  marginBottom: theme.spacing.lg
                }}>Recent Activity</h3>
                <div style={{
                  background: theme.colors.neutral.card,
                  boxShadow: theme.components.card.shadow,
                  borderRadius: theme.components.table.borderRadius,
                  overflow: 'hidden'
                }}>
                  <ul style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0
                  }}>
                    {quotes.slice(0, 5).map(quote => (
                      <li key={quote.id} style={{
                        borderBottom: `1px solid ${theme.colors.neutral.border}`
                      }}>
                        <Link 
                          to={`/agent/quotes/${quote.id}`} 
                          style={{
                            display: 'block',
                            padding: theme.components.table.cellPadding,
                            textDecoration: 'none',
                            background: hoveredQuoteId === quote.id ? theme.colors.neutral.hover : 'transparent',
                            transition: `background ${theme.transitions.fast}`
                          }}
                          onMouseEnter={() => setHoveredQuoteId(quote.id)}
                          onMouseLeave={() => setHoveredQuoteId(null)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <p style={{
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.medium,
                                color: theme.colors.text.primary,
                                marginBottom: '4px'
                              }}>
                                Quote #{quote.quote_number || quote.id} - {quote.client_name || 'Unknown Client'}
                              </p>
                              <p style={{
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.secondary
                              }}>
                                {theme.formatCurrency(quote.total_amount)} • {theme.formatDate(quote.created_at)}
                              </p>
                            </div>
                            <span style={{
                              padding: theme.components.badge.padding,
                              fontSize: theme.components.badge.fontSize,
                              fontWeight: theme.components.badge.fontWeight,
                              borderRadius: theme.components.badge.borderRadius,
                              ...theme.getStatusBadgeStyle(quote.status)
                            }}>
                              {quote.status}
                            </span>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right Column: Quick Actions Sidebar */}
              <div>
                <h3 style={{
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.semibold,
                  color: theme.colors.text.primary,
                  marginBottom: theme.spacing.lg
                }}>Quick Actions</h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing.md
                }}>
                <button
                  onClick={() => navigate('/agent/quotes/new')}
                  style={{
                    background: theme.components.button.primary.background,
                    color: theme.components.button.primary.color,
                    borderRadius: theme.components.button.primary.borderRadius,
                    padding: theme.components.button.primary.padding,
                    height: theme.components.button.primary.height,
                    fontSize: theme.components.button.primary.fontSize,
                    fontWeight: theme.components.button.primary.fontWeight,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: theme.spacing.sm,
                    transition: `background ${theme.transitions.normal}`
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.components.button.primary.hover}
                  onMouseLeave={(e) => e.currentTarget.style.background = theme.components.button.primary.background}
                >
                  <svg style={{ height: '20px', width: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New Quote
                </button>
                <button
                  onClick={() => navigate('/agent/clients')}
                  style={{
                    background: '#64748B',
                    color: theme.components.button.primary.color,
                    borderRadius: theme.components.button.primary.borderRadius,
                    padding: theme.components.button.primary.padding,
                    height: theme.components.button.primary.height,
                    fontSize: theme.components.button.primary.fontSize,
                    fontWeight: theme.components.button.primary.fontWeight,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: theme.spacing.sm,
                    transition: `background ${theme.transitions.normal}`
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#64748B'}
                >
                  <svg style={{ height: '20px', width: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Manage Clients
                </button>
                <button
                  onClick={() => navigate('/agent/commissions')}
                  style={{
                    background: theme.colors.status.success,
                    color: theme.components.button.primary.color,
                    borderRadius: theme.components.button.primary.borderRadius,
                    padding: theme.components.button.primary.padding,
                    height: theme.components.button.primary.height,
                    fontSize: theme.components.button.primary.fontSize,
                    fontWeight: theme.components.button.primary.fontWeight,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: theme.spacing.sm,
                    position: 'relative',
                    transition: `background ${theme.transitions.normal}`
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.background = theme.colors.status.success}
                >
                  <svg style={{ height: '20px', width: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Submit Commission
                  {commissions.filter(c => c.has_unread_messages).length > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: theme.colors.status.error,
                      color: theme.colors.text.white,
                      fontSize: theme.typography.fontSize.xs,
                      borderRadius: '50%',
                      height: '24px',
                      width: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {commissions.filter(c => c.has_unread_messages).length}
                    </span>
                  )}
                </button>
                </div>
              </div>
            </div>
    </div>
  );
};

export default AgentDashboard;
